import { createBrowserRouter, Navigate, RouteObject } from 'react-router-dom';
import AdminGuard from 'src/guards/AdminGuard';
import AuthGuard from 'src/guards/AuthGuard';
import GuestGuard from 'src/guards/GuestGuard';
import { DashboardLayout } from 'src/layouts';
import BasicLayout from 'src/layouts/basic/BasicLayout';

import {
  ChangeLanguage,
  ChangePassword,
  ExistId,
  FindId,
  FindIdEnd,
  Home,
  HospitalManagement,
  Login,
  MemberWithdrawal,
  Page403,
  Page404,
  Page500,
  PageError,
  PatientChart,
  PatientDiary,
  PatientManagement,
  ProfileManagement,
  Register,
  RegisterEnd,
  RegisterForm,
  ResetPwd,
  ResetPwdEnd,
  ResetPwdForm,
  ScenarioManagement,
  ServiceControl,
  TermsManagement,
  UserManual,
} from './elements';
import { PATH_AUTH, PATH_PAGE } from './paths';

// ----------------------------------------------------------------------

const routes: RouteObject[] = [
  {
    children: [
      {
        errorElement: <PageError />,
        children: [
          {
            path: PATH_PAGE.page403,
            element: <Page403 />,
          },
          {
            path: PATH_PAGE.page404,
            element: <Page404 />,
          },
          {
            path: PATH_PAGE.page500,
            element: <Page500 />,
          },
          { path: '*', element: <Navigate to={PATH_PAGE.page404} replace /> },
          {
            path: PATH_AUTH.login,
            element: (
              <GuestGuard>
                <Login />
              </GuestGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    element: (
      <GuestGuard>
        <BasicLayout />
      </GuestGuard>
    ),
    children: [
      {
        errorElement: <PageError />,
        children: [
          {
            path: PATH_AUTH.findId.root,
            element: <FindId />,
          },
          {
            path: PATH_AUTH.findId.end,
            element: <FindIdEnd />,
          },
          {
            path: PATH_AUTH.resetPwd.root,
            element: <ResetPwd />,
          },
          {
            path: PATH_AUTH.resetPwd.form,
            element: <ResetPwdForm />,
          },
          {
            path: PATH_AUTH.resetPwd.end,
            element: <ResetPwdEnd />,
          },
          {
            path: PATH_AUTH.register.root,
            element: <Register />,
          },
          {
            path: PATH_AUTH.register.existId,
            element: <ExistId />,
          },
          {
            path: PATH_AUTH.register.form,
            element: <RegisterForm />,
          },
          {
            path: PATH_AUTH.register.end,
            element: <RegisterEnd />,
          },
        ],
      },
    ],
  },
  {
    children: [
      {
        errorElement: <PageError />,
        element: (
          <AuthGuard>
            <DashboardLayout />
          </AuthGuard>
        ),
        children: [
          {
            path: PATH_PAGE.home,
            element: <Home />,
          },
          {
            path: PATH_PAGE.patientManagement.root,
            element: <PatientManagement />,
          },
          {
            path: PATH_PAGE.patientManagement.chart,
            element: <PatientChart />,
          },
          {
            path: PATH_PAGE.patientManagement.diary,
            element: <PatientDiary />,
          },
          {
            path: PATH_PAGE.scenarioManagement,
            element: (
              <AdminGuard>
                <ScenarioManagement />
              </AdminGuard>
            ),
          },
          {
            path: PATH_PAGE.serviceControl,
            element: (
              <AdminGuard>
                <ServiceControl />
              </AdminGuard>
            ),
          },
          {
            path: PATH_PAGE.termsManagement,
            element: (
              <AdminGuard>
                <TermsManagement />
              </AdminGuard>
            ),
          },
          {
            path: PATH_PAGE.hospitalManagement,
            element: (
              <AdminGuard>
                <HospitalManagement />
              </AdminGuard>
            ),
          },
        ],
      },
    ],
  },
  {
    children: [
      {
        errorElement: <PageError />,
        element: (
          <AuthGuard>
            <DashboardLayout needClipboard={false} />
          </AuthGuard>
        ),
        children: [
          {
            path: PATH_PAGE.myPage.profileManagement,
            element: <ProfileManagement />,
          },
          {
            path: PATH_PAGE.myPage.changePassword,
            element: <ChangePassword />,
          },
          {
            path: PATH_PAGE.myPage.changeLanguage,
            element: <ChangeLanguage />,
          },
          {
            path: PATH_PAGE.myPage.memberWithdrawal,
            element: <MemberWithdrawal />,
          },
        ],
      },
    ],
  },
  {
    errorElement: <PageError />,
    path: PATH_PAGE.userManual,
    element: <UserManual />,
  },
];

const router = createBrowserRouter(routes, {
  basename: '/',
  future: {
    v7_fetcherPersist: true,
    v7_relativeSplatPath: true,
    v7_normalizeFormMethod: true,
    v7_partialHydration: true,
    v7_skipActionErrorRevalidation: true, // v7의 동작 미리 적용
  },
});

export default router;
